import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { FileTree } from "@/components/files/FileTree";
import { FolderOpen } from "lucide-react";
import { cn } from "@/lib/utils";

export default function FilesApp() {
  const { has } = useAtlas();
  const canManage = has("clients.manage");
  const [trees, setTrees] = useState(null);
  const [sel, setSel] = useState(null);

  useEffect(() => { api.get("/files-app/trees").then((r) => { setTrees(r.data); setSel(r.data[0] || null); }).catch(() => setTrees([])); }, []);

  const download = async (f) => {
    const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
    const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = f.name; a.click(); setTimeout(() => URL.revokeObjectURL(u), 5000);
  };
  const load = (parentId, q) => api.get(`/files-app/${sel.workspace_id}/tree`, { params: { parent_id: parentId || undefined, q: q || undefined } }).then((r) => r.data);
  const ops = sel ? {
    createFolder: (name, parent_id) => api.post(`/files-app/${sel.workspace_id}/folders`, { name, parent_id }),
    deleteFolder: (fid) => api.delete(`/files-app/folders/${fid}`),
    upload: (file, parent_id) => { const fd = new FormData(); fd.append("file", file); if (parent_id) fd.append("parent_id", parent_id); return api.post(`/files-app/${sel.workspace_id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); },
    addLink: (url, name, parent_id) => api.post(`/files-app/${sel.workspace_id}/links`, { url, name, parent_id }),
    moveFile: (fileId, folder_id) => api.put(`/files-app/files/${fileId}/move`, { folder_id }),
    moveFolder: (fid, parent_id) => api.put(`/files-app/folders/${fid}`, { parent_id }),
    setReminder: (fileId, expiry_date) => api.put(`/files/${fileId}/reminder`, { expiry_date }),
    deleteFile: (f) => api.delete(`/files/${f.id}`),
  } : {};

  if (!trees) return <div className="text-slate-400 text-sm">Loading…</div>;

  return (
    <div data-testid="files-app">
      <PageHeader title="Files" subtitle="Every client's documents, contracts and links — organised in folders" />
      {trees.length === 0 ? (
        <EmptyState icon={FolderOpen} title="No client file trees yet" description="Convert an organization into a client workspace to give it a file area." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
          <aside className="space-y-1" data-testid="files-tree-list">
            {trees.map((t) => (
              <button key={t.workspace_id} onClick={() => setSel(t)} data-testid={`files-tree-${t.workspace_id}`}
                className={cn("w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm border", sel?.workspace_id === t.workspace_id ? "bg-white border-slate-300 shadow-sm" : "border-transparent hover:bg-slate-100")}>
                {t.logo ? <img src={t.logo} alt="" className="w-7 h-7 rounded-lg object-cover" /> : <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ background: t.accent || "#0f766e" }}>{(t.name || "C")[0]}</span>}
                <span className="flex-1 truncate font-medium text-slate-800">{t.name}</span>
                <span className="text-xs text-slate-400">{t.file_count}</span>
              </button>
            ))}
          </aside>
          <section className="min-w-0">
            {sel && <FileTree key={sel.workspace_id} load={load} canManage={canManage} ops={ops} download={download} accent={sel.accent || "#0f766e"} testid="files-app-tree" />}
          </section>
        </div>
      )}
    </div>
  );
}
